// T021: Main App Component

import { useCallback, useState, useEffect, useMemo } from 'react';
import Phaser from 'phaser';
import { GameContainer, ChoiceOverlay, ResponseOverlay, ResponseTestPanel, VictoryScreen, GameLogPanel, TutorialOverlay, AuthScreen, LobbyScreen, ReplayViewer, GameHistoryScreen, AdminLayout, ShopScreen, QuestPanel } from './ui/components';
import { useUIStore, useDeckStore, useGameStore } from './store';
import { useResponsePhase } from './ui/hooks';
import { initializeResponseIntegration } from './core/response';
import type { CardDefinition, Faction } from './types';
import { CardLoader } from './core/data/CardLoader';
import { getLogger } from './utils/logger';

const log = getLogger('App');

/**
 * Main application component.
 * Manages screen navigation and integrates Phaser with React.
 */
export function App() {
  const { currentScreen } = useUIStore();

  const handleGameReady = useCallback((game: Phaser.Game) => {
    log.debug('Phaser game ready:', game);
    // Game instance is now available for store integration
  }, []);

  return (
    <div className="app" style={styles.app}>
      {currentScreen === 'auth' && (
        <AuthScreen />
      )}
      {currentScreen === 'lobby' && (
        <LobbyScreen />
      )}
      {currentScreen === 'mainMenu' && (
        <MainMenu />
      )}
      {currentScreen === 'deckBuilder' && (
        <DeckBuilder />
      )}
      {currentScreen === 'game' && (
        <GameScreen onGameReady={handleGameReady} />
      )}
      {currentScreen === 'victory' && (
        <VictoryScreen />
      )}
      {currentScreen === 'replay' && (
        <ReplayViewer />
      )}
      {currentScreen === 'history' && (
        <GameHistoryScreen />
      )}
      {currentScreen === 'admin' && (
        <AdminLayout />
      )}
      {currentScreen === 'shop' && (
        <ShopScreen />
      )}
      {currentScreen === 'quests' && (
        <QuestPanel />
      )}
    </div>
  );
}

/**
 * Game screen with response phase overlay.
 */
function GameScreen({ onGameReady }: { onGameReady?: (game: Phaser.Game) => void }) {
  const {
    isActive,
    triggerCardName,
    triggerPlayerId,
    currentPriority,
    availableResponses,
    chainEntries,
    timeRemaining,
    waitingForAI,
    respond,
    pass,
  } = useResponsePhase();

  const gameState = useGameStore.getState().gameState;
  const localPlayerId = gameState?.player1.id ?? '';

  // 初始化回應系統（當遊戲開始時）
  useEffect(() => {
    const { gameState: state, cardDefinitions } = useGameStore.getState();
    if (state && cardDefinitions.size > 0) {
      // 初始化回應系統，player2 是 AI
      initializeResponseIntegration(
        cardDefinitions,
        state.player1.id,
        state.player2.id,
        true // 啟用回應階段
      );
      log.debug('回應系統已初始化');
    }
  }, []);

  return (
    <>
      <GameContainer onGameReady={onGameReady} />
      <ChoiceOverlay />
      <ResponseOverlay
        isOpen={isActive}
        triggerCardName={triggerCardName}
        triggerPlayerId={triggerPlayerId}
        currentPriority={currentPriority}
        localPlayerId={localPlayerId}
        availableResponses={availableResponses}
        chainEntries={chainEntries}
        timeRemaining={timeRemaining}
        maxTime={5}
        waitingForAI={waitingForAI}
        onRespond={respond}
        onPass={pass}
      />
      {/* 遊戲日誌 */}
      <GameLogPanel />
      {/* 教學疊層 */}
      <TutorialOverlay />
      {/* 測試面板 - 開發時使用 */}
      {import.meta.env.DEV && <ResponseTestPanel />}
    </>
  );
}

/**
 * Main menu screen component — polished card game aesthetic.
 */
function MainMenu() {
  const { navigateTo } = useUIStore();

  return (
    <div style={menuStyles.wrapper}>
      {/* Starry background layer */}
      <div style={menuStyles.starsBg} />

      {/* Logo / Title */}
      <div style={menuStyles.logoSection}>
        <img
          src="assets/ui/logo.png"
          alt="星靈王"
          style={menuStyles.logo}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <h1 style={menuStyles.title}>星靈王</h1>
        <p style={menuStyles.subtitle}>Xinglingwang Card Battle</p>
      </div>

      {/* Main buttons */}
      <div style={menuStyles.buttonContainer}>
        <button style={menuStyles.primaryBtn} onClick={() => { navigateTo('game'); }}>
          ⚔️ 開始遊戲
        </button>
        <button style={menuStyles.primaryBtn} onClick={() => { navigateTo('auth'); }}>
          🌐 線上對戰
        </button>
        <button style={menuStyles.secondaryBtn} onClick={() => { navigateTo('deckBuilder'); }}>
          🃏 牌組構築
        </button>
        <div style={menuStyles.buttonRow}>
          <button style={menuStyles.smallBtn} onClick={() => { navigateTo('history'); }}>
            📜 對戰歷史
          </button>
          <button style={{ ...menuStyles.smallBtn, backgroundColor: '#3a2a10' }} onClick={() => { navigateTo('shop'); }}>
            🎴 商城
          </button>
        </div>
        <div style={menuStyles.buttonRow}>
          <button style={{ ...menuStyles.smallBtn, backgroundColor: '#1a2a3a' }} onClick={() => { navigateTo('quests'); }}>
            📋 每日任務
          </button>
          <button style={{ ...menuStyles.smallBtn, backgroundColor: '#2a1a3a' }} onClick={() => { navigateTo('admin'); }}>
            ⚙️ 管理後台
          </button>
        </div>
      </div>

      {/* Version */}
      <div style={menuStyles.version}>v0.1.0</div>
    </div>
  );
}

const menuStyles: Record<string, React.CSSProperties> = {
  wrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100vw',
    height: '100vh',
    background: 'radial-gradient(ellipse at center, #1a1040 0%, #060618 70%)',
    overflow: 'hidden',
  },
  starsBg: {
    position: 'absolute',
    inset: 0,
    backgroundImage: 'url(assets/backgrounds/starry-bg.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    opacity: 0.3,
    pointerEvents: 'none',
  },
  logoSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '32px',
    zIndex: 1,
  },
  logo: {
    width: '200px',
    height: 'auto',
    marginBottom: '8px',
    filter: 'drop-shadow(0 0 20px rgba(255,215,0,0.3))',
  },
  title: {
    color: '#ffd700',
    fontSize: '52px',
    margin: 0,
    fontFamily: 'Arial, sans-serif',
    textShadow: '0 0 20px rgba(255,215,0,0.4), 0 2px 4px rgba(0,0,0,0.8)',
    letterSpacing: '8px',
  },
  subtitle: {
    color: '#8899bb',
    fontSize: '16px',
    margin: '4px 0 0 0',
    letterSpacing: '4px',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    zIndex: 1,
  },
  primaryBtn: {
    width: '280px',
    padding: '16px 0',
    fontSize: '20px',
    fontWeight: 'bold',
    backgroundColor: '#2a2040',
    color: '#ffd700',
    border: '2px solid #8b6914',
    borderRadius: '8px',
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
    letterSpacing: '3px',
    transition: 'all 0.15s ease',
    boxShadow: '0 0 12px rgba(139,105,20,0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
  },
  secondaryBtn: {
    width: '280px',
    padding: '14px 0',
    fontSize: '18px',
    backgroundColor: '#1a1a30',
    color: '#aabbdd',
    border: '1px solid #4a4a6a',
    borderRadius: '8px',
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
    letterSpacing: '2px',
    transition: 'all 0.15s ease',
  },
  buttonRow: {
    display: 'flex',
    gap: '12px',
  },
  smallBtn: {
    width: '134px',
    padding: '12px 0',
    fontSize: '14px',
    backgroundColor: '#1a1a2a',
    color: '#8899aa',
    border: '1px solid #3a3a5a',
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
    transition: 'all 0.15s ease',
  },
  version: {
    position: 'absolute',
    bottom: '12px',
    right: '16px',
    color: '#444466',
    fontSize: '12px',
    zIndex: 1,
  },
};

// Faction display configuration
const FACTION_CONFIG: Record<string, { emoji: string; color: string }> = {
  fairy: { emoji: '🧚', color: '#88ff88' },
  robot: { emoji: '🤖', color: '#888899' },
  skeleton: { emoji: '💀', color: '#cccccc' },
  virtual: { emoji: '💻', color: '#44dddd' },
  artwork: { emoji: '🎨', color: '#dd88dd' },
  orc: { emoji: '👹', color: '#448844' },
  candy: { emoji: '🍬', color: '#ff88cc' },
  savage: { emoji: '🦴', color: '#884422' },
  traveler: { emoji: '🎒', color: '#8888ff' },
  monster: { emoji: '👻', color: '#8844aa' },
  steam: { emoji: '⚙️', color: '#996633' },
  goat: { emoji: '🐐', color: '#ddddaa' },
  hero: { emoji: '⚔️', color: '#ffdd44' },
  cat: { emoji: '🐱', color: '#ffcc88' },
  carbon: { emoji: '⚫', color: '#333333' },
  power_beast: { emoji: '⚡', color: '#ffaa00' },
  island: { emoji: '🏝️', color: '#44aaff' },
  circus: { emoji: '🎪', color: '#ff4488' },
  neutral_spell: { emoji: '✨', color: '#666666' },
  neutral_unit: { emoji: '👤', color: '#555555' },
};

/**
 * Deck builder screen with full functionality.
 */
function DeckBuilder() {
  const { goBack } = useUIStore();
  const {
    selectedFaction,
    currentDeckCards,
    availableCards,
    savedDecks,
    selectFaction,
    addCardToDeck,
    removeCardFromDeck,
    clearDeck,
    validateDeck,
    saveDeck,
    loadDeck,
    deleteDeck,
    getDeckStats,
    initialize,
  } = useDeckStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'unit' | 'spell'>('all');
  const [deckName, setDeckName] = useState('');
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Initialize store on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Get playable factions
  const factions = useMemo(() => CardLoader.getPlayableFactions(), []);

  // Get available cards for the selected faction
  const displayCards = useMemo(() => {
    if (!selectedFaction) return [];

    let cards = availableCards;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      cards = cards.filter(
        (c) =>
          c.nameZh.toLowerCase().includes(query) ||
          c.name.toLowerCase().includes(query) ||
          (c.code?.toLowerCase().includes(query) ?? false)
      );
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      cards = cards.filter((c) => c.type === typeFilter);
    }

    // Sort by faction (selected first), then by type, then by name
    return [...cards].sort((a, b) => {
      // Selected faction first
      if (a.faction === selectedFaction && b.faction !== selectedFaction) return -1;
      if (a.faction !== selectedFaction && b.faction === selectedFaction) return 1;
      // Then by type
      if (a.type !== b.type) return a.type.localeCompare(b.type);
      // Then by name
      return a.nameZh.localeCompare(b.nameZh);
    });
  }, [selectedFaction, availableCards, searchQuery, typeFilter]);

  // Get deck card counts (for showing duplicates)
  const deckCardCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const card of currentDeckCards) {
      counts.set(card.id, (counts.get(card.id) ?? 0) + 1);
    }
    return counts;
  }, [currentDeckCards]);

  // Stats
  const stats = getDeckStats();
  const validation = validateDeck();

  // Show temporary message
  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage({ text, type });
    setTimeout(() => { setMessage(null); }, 2000);
  };

  // Handle card add
  const handleAddCard = (card: CardDefinition) => {
    const result = addCardToDeck(card.id);
    if (!result.canAdd) {
      const errorMessages: Record<string, string> = {
        DECK_FULL: '牌組已滿 (20張)',
        DUPLICATE: '此卡不可重複放入',
        OVERLOAD_MAX: '超載卡最多放入3張',
        WRONG_FACTION: '陣營不符',
        MANA_EXCEEDED: '魔力值超過上限 (15)',
      };
      showMessage(errorMessages[result.reason ?? ''] ?? '無法加入', 'error');
    }
  };

  // Handle save
  const handleSave = () => {
    if (!deckName.trim()) {
      showMessage('請輸入牌組名稱', 'error');
      return;
    }
    if (!validation.isValid) {
      showMessage('牌組不完整，無法儲存', 'error');
      return;
    }
    const id = saveDeck(deckName.trim());
    if (id) {
      showMessage('牌組已儲存', 'success');
      setDeckName('');
    } else {
      showMessage('儲存失敗', 'error');
    }
  };

  // Handle load
  const handleLoad = (deckId: string) => {
    loadDeck(deckId);
    showMessage('牌組已載入', 'success');
  };

  // Handle delete
  const handleDelete = (deckId: string) => {
    deleteDeck(deckId);
    showMessage('牌組已刪除', 'success');
  };

  return (
    <div style={deckStyles.container}>
      {/* Header */}
      <div style={deckStyles.header}>
        <button style={deckStyles.backButton} onClick={goBack}>
          ← 返回
        </button>
        <h1 style={deckStyles.title}>牌組構築</h1>
        <div style={deckStyles.headerRight}>
          {message && (
            <span
              style={{
                ...deckStyles.message,
                color: message.type === 'success' ? '#44ff44' : '#ff4444',
              }}
            >
              {message.text}
            </span>
          )}
        </div>
      </div>

      {/* Faction Selection (if no faction selected) */}
      {!selectedFaction && (
        <div style={deckStyles.factionSelection}>
          <h2 style={deckStyles.sectionTitle}>選擇陣營</h2>
          <div style={deckStyles.factionGrid}>
            {factions.map((faction) => {
              const config = FACTION_CONFIG[faction.id] ?? { emoji: '❓', color: '#888888' };
              return (
                <button
                  key={faction.id}
                  style={{
                    ...deckStyles.factionButton,
                    borderColor: config.color,
                  }}
                  onClick={() => { selectFaction(faction.id); }}
                >
                  <span style={{ fontSize: '32px' }}>{config.emoji}</span>
                  <span>{faction.nameZh}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Main deck builder UI (when faction selected) */}
      {selectedFaction && (
        <div style={deckStyles.mainContent}>
          {/* Left: Card Pool */}
          <div style={deckStyles.cardPool}>
            <div style={deckStyles.poolHeader}>
              <h3 style={deckStyles.sectionTitle}>
                {FACTION_CONFIG[selectedFaction].emoji}{' '}
                {factions.find((f) => f.id === selectedFaction)?.nameZh ?? selectedFaction}
              </h3>
              <button
                style={deckStyles.changeFactionButton}
                onClick={() => {
                  clearDeck();
                  selectFaction(null as unknown as Faction);
                }}
              >
                更換陣營
              </button>
            </div>

            {/* Search & Filters */}
            <div style={deckStyles.filters}>
              <input
                type="text"
                placeholder="搜尋卡牌..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); }}
                style={deckStyles.searchInput}
              />
              <div style={deckStyles.typeFilters}>
                <button
                  style={{
                    ...deckStyles.filterButton,
                    backgroundColor: typeFilter === 'all' ? '#4a6a8a' : '#3a3a5a',
                  }}
                  onClick={() => { setTypeFilter('all'); }}
                >
                  全部
                </button>
                <button
                  style={{
                    ...deckStyles.filterButton,
                    backgroundColor: typeFilter === 'unit' ? '#4a6a8a' : '#3a3a5a',
                  }}
                  onClick={() => { setTypeFilter('unit'); }}
                >
                  單位
                </button>
                <button
                  style={{
                    ...deckStyles.filterButton,
                    backgroundColor: typeFilter === 'spell' ? '#4a6a8a' : '#3a3a5a',
                  }}
                  onClick={() => { setTypeFilter('spell'); }}
                >
                  魔法
                </button>
              </div>
            </div>

            {/* Card List */}
            <div style={deckStyles.cardList}>
              {displayCards.map((card) => (
                <CardPoolItem
                  key={card.id}
                  card={card}
                  count={deckCardCounts.get(card.id) ?? 0}
                  onAdd={() => { handleAddCard(card); }}
                />
              ))}
              {displayCards.length === 0 && (
                <div style={deckStyles.emptyMessage}>沒有符合條件的卡牌</div>
              )}
            </div>
          </div>

          {/* Right: Current Deck */}
          <div style={deckStyles.deckPanel}>
            <div style={deckStyles.deckHeader}>
              <h3 style={deckStyles.sectionTitle}>目前牌組</h3>
              <span
                style={{
                  ...deckStyles.deckCount,
                  color: stats.total === 20 ? '#44ff44' : stats.total > 20 ? '#ff4444' : '#ffcc00',
                }}
              >
                {stats.total}/20
              </span>
            </div>

            {/* Deck Stats */}
            <div style={deckStyles.deckStats}>
              <span>陣營卡: {stats.factionCards}</span>
              <span>中立卡: {stats.neutralCards}</span>
              <span style={{ color: stats.mana > 15 ? '#ff4444' : '#888888' }}>
                魔力值: {stats.mana}/15
              </span>
            </div>

            {/* Validation Errors */}
            {!validation.isValid && (
              <div style={deckStyles.validationErrors}>
                {validation.errors.map((error, i) => (
                  <div key={i} style={deckStyles.errorItem}>
                    ⚠️ {getErrorMessage(error)}
                  </div>
                ))}
              </div>
            )}

            {/* Deck Cards */}
            <div style={deckStyles.deckList}>
              {currentDeckCards.map((card, index) => (
                <DeckListItem
                  key={`${card.id}-${String(index)}`}
                  card={card}
                  onRemove={() => { removeCardFromDeck(card.id); }}
                />
              ))}
              {currentDeckCards.length === 0 && (
                <div style={deckStyles.emptyMessage}>點擊左側卡牌加入牌組</div>
              )}
            </div>

            {/* Deck Actions */}
            <div style={deckStyles.deckActions}>
              <button style={deckStyles.clearButton} onClick={clearDeck}>
                清空牌組
              </button>
            </div>

            {/* Save/Load Section */}
            <div style={deckStyles.saveSection}>
              <h4 style={deckStyles.subTitle}>儲存牌組</h4>
              <div style={deckStyles.saveRow}>
                <input
                  type="text"
                  placeholder="牌組名稱"
                  value={deckName}
                  onChange={(e) => { setDeckName(e.target.value); }}
                  style={deckStyles.deckNameInput}
                />
                <button
                  style={{
                    ...deckStyles.saveButton,
                    opacity: validation.isValid ? 1 : 0.5,
                  }}
                  onClick={handleSave}
                  disabled={!validation.isValid}
                >
                  儲存
                </button>
              </div>

              {/* Saved Decks */}
              {savedDecks.length > 0 && (
                <div style={deckStyles.savedDecks}>
                  <h4 style={deckStyles.subTitle}>已儲存的牌組</h4>
                  {savedDecks.map((deck) => (
                    <div key={deck.id} style={deckStyles.savedDeckItem}>
                      <span>
                        {FACTION_CONFIG[deck.faction].emoji} {deck.name}
                      </span>
                      <div style={deckStyles.savedDeckActions}>
                        <button
                          style={deckStyles.loadButton}
                          onClick={() => { handleLoad(deck.id); }}
                        >
                          載入
                        </button>
                        <button
                          style={deckStyles.deleteButton}
                          onClick={() => { handleDelete(deck.id); }}
                        >
                          刪除
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Card item in the card pool.
 */
function CardPoolItem({
  card,
  count,
  onAdd,
}: {
  card: CardDefinition;
  count: number;
  onAdd: () => void;
}) {
  const isNeutral = card.faction === 'neutral_spell' || card.faction === 'neutral_unit';
  const config = FACTION_CONFIG[card.faction] ?? { emoji: '❓', color: '#888888' };

  return (
    <div
      style={{
        ...deckStyles.cardItem,
        borderLeftColor: isNeutral ? '#666666' : config.color,
        opacity: count >= 3 ? 0.5 : 1,
      }}
      onClick={onAdd}
    >
      <div style={deckStyles.cardInfo}>
        <span style={deckStyles.cardName}>
          {card.nameZh}
          {count > 0 && <span style={deckStyles.cardCount}> ×{count}</span>}
        </span>
        <span style={deckStyles.cardDetails}>
          {card.type === 'unit' ? `ATK ${String(card.atk ?? 0)} / ${String(card.score ?? 0)}分` : `魔 ${String(card.cost ?? 0)}`}
          {isNeutral && ' (中立)'}
        </span>
      </div>
      <span style={deckStyles.addButton}>+</span>
    </div>
  );
}

/**
 * Card item in the current deck list.
 */
function DeckListItem({
  card,
  onRemove,
}: {
  card: CardDefinition;
  onRemove: () => void;
}) {
  const isNeutral = card.faction === 'neutral_spell' || card.faction === 'neutral_unit';
  const config = FACTION_CONFIG[card.faction] ?? { emoji: '❓', color: '#888888' };

  return (
    <div
      style={{
        ...deckStyles.deckItem,
        borderLeftColor: isNeutral ? '#666666' : config.color,
      }}
    >
      <span style={deckStyles.cardName}>{card.nameZh}</span>
      <button style={deckStyles.removeButton} onClick={onRemove}>
        ×
      </button>
    </div>
  );
}

/**
 * Get human-readable error message.
 */
function getErrorMessage(error: string): string {
  const messages: Record<string, string> = {
    INVALID_SIZE: '牌組需要剛好20張卡牌',
    NO_FACTION_CARDS: '需要至少1張陣營卡',
    DUPLICATE_NON_OVERLOAD: '非超載卡不可重複',
    OVERLOAD_EXCEEDED: '超載卡超過3張上限',
    WRONG_FACTION: '包含不屬於此陣營的卡牌',
    MANA_EXCEEDED: '魔力值超過15點上限',
  };
  return messages[error] ?? error;
}

const styles: Record<string, React.CSSProperties> = {
  app: {
    width: '100vw',
    height: '100vh',
    backgroundColor: '#060618',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
};

// Deck builder styles
const deckStyles: Record<string, React.CSSProperties> = {
  container: {
    width: '100vw',
    height: '100vh',
    backgroundColor: '#1a1a2e',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Arial, sans-serif',
    color: '#ffffff',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#252545',
    borderBottom: '1px solid #3a3a5a',
  },
  backButton: {
    padding: '8px 16px',
    fontSize: '14px',
    backgroundColor: '#3a3a5a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: '0 0 0 24px',
  },
  headerRight: {
    marginLeft: 'auto',
  },
  message: {
    fontSize: '14px',
    fontWeight: 'bold',
  },
  factionSelection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    margin: '0 0 16px 0',
    color: '#ffffff',
  },
  factionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '16px',
    maxWidth: '900px',
  },
  factionButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '16px',
    backgroundColor: '#2a2a4a',
    border: '2px solid',
    borderRadius: '8px',
    cursor: 'pointer',
    color: '#ffffff',
    fontSize: '14px',
    minWidth: '100px',
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  cardPool: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid #3a3a5a',
    padding: '16px',
  },
  poolHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  changeFactionButton: {
    padding: '6px 12px',
    fontSize: '12px',
    backgroundColor: '#3a3a5a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  filters: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
  },
  searchInput: {
    flex: 1,
    padding: '8px 12px',
    fontSize: '14px',
    backgroundColor: '#2a2a4a',
    color: '#ffffff',
    border: '1px solid #3a3a5a',
    borderRadius: '4px',
    outline: 'none',
  },
  typeFilters: {
    display: 'flex',
    gap: '4px',
  },
  filterButton: {
    padding: '8px 12px',
    fontSize: '12px',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  cardList: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  cardItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: '#2a2a4a',
    borderLeft: '3px solid',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  cardInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  cardName: {
    fontSize: '14px',
    fontWeight: 'bold',
  },
  cardCount: {
    color: '#ffcc00',
    fontSize: '12px',
  },
  cardDetails: {
    fontSize: '11px',
    color: '#888888',
  },
  addButton: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#44ff44',
  },
  emptyMessage: {
    textAlign: 'center',
    color: '#666666',
    padding: '40px',
    fontSize: '14px',
  },
  deckPanel: {
    width: '350px',
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    backgroundColor: '#1e1e3e',
  },
  deckHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  deckCount: {
    fontSize: '18px',
    fontWeight: 'bold',
  },
  deckStats: {
    display: 'flex',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#888888',
  },
  validationErrors: {
    marginBottom: '12px',
    padding: '8px',
    backgroundColor: '#3a2a2a',
    borderRadius: '4px',
  },
  errorItem: {
    fontSize: '12px',
    color: '#ff8888',
    marginBottom: '4px',
  },
  deckList: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginBottom: '12px',
  },
  deckItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 10px',
    backgroundColor: '#2a2a4a',
    borderLeft: '3px solid',
    borderRadius: '4px',
    fontSize: '13px',
  },
  removeButton: {
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    backgroundColor: '#4a3a3a',
    color: '#ff8888',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  deckActions: {
    marginBottom: '16px',
  },
  clearButton: {
    width: '100%',
    padding: '8px',
    fontSize: '14px',
    backgroundColor: '#4a3a3a',
    color: '#ff8888',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  saveSection: {
    borderTop: '1px solid #3a3a5a',
    paddingTop: '16px',
  },
  subTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    margin: '0 0 8px 0',
    color: '#cccccc',
  },
  saveRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
  },
  deckNameInput: {
    flex: 1,
    padding: '8px 12px',
    fontSize: '14px',
    backgroundColor: '#2a2a4a',
    color: '#ffffff',
    border: '1px solid #3a3a5a',
    borderRadius: '4px',
    outline: 'none',
  },
  saveButton: {
    padding: '8px 16px',
    fontSize: '14px',
    backgroundColor: '#3a5a3a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  savedDecks: {
    marginTop: '12px',
  },
  savedDeckItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px',
    backgroundColor: '#2a2a4a',
    borderRadius: '4px',
    marginBottom: '4px',
    fontSize: '13px',
  },
  savedDeckActions: {
    display: 'flex',
    gap: '4px',
  },
  loadButton: {
    padding: '4px 8px',
    fontSize: '11px',
    backgroundColor: '#3a5a5a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  deleteButton: {
    padding: '4px 8px',
    fontSize: '11px',
    backgroundColor: '#5a3a3a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
};
